// NetworkTab Component
// Unified view of all business connections - customers, prospects, investors, partners

import React, { useState, useEffect, useRef } from 'react';
import {
  UserPlus,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Send,
  Trash2,
  Users,
  Building2,
  TrendingUp,
  Briefcase,
  Download,
  Search,
  X,
  Filter,
  Calendar,
  Link as LinkIcon,
  Edit3,
  MoreVertical
} from 'lucide-react';
import toast from 'react-hot-toast';
import { invitationService } from '../services/invitationService';
import { supabase } from '../lib/supabase';
import styles from './NetworkTab.module.css';

const RELATIONSHIP_TYPES = [
  {
    value: 'client', label: 'Client', icon: Users,
    iconBgClass: 'relIconBg', iconClass: 'relIcon',
    badgeClass: 'relBadge',
    filterActiveClass: 'relFilterActive', filterInactiveClass: 'relFilterInactive',
    selectorActiveClass: 'relSelectorActive', selectorIconClass: 'relSelectorIcon', selectorLabelClass: 'relSelectorLabel'
  },
  {
    value: 'prospect', label: 'Prospect', icon: TrendingUp,
    iconBgClass: 'relIconBgSuccess', iconClass: 'relIconSuccess',
    badgeClass: 'relBadgeSuccess',
    filterActiveClass: 'relFilterActiveSuccess', filterInactiveClass: 'relFilterInactiveSuccess',
    selectorActiveClass: 'relSelectorActiveSuccess', selectorIconClass: 'relSelectorIconSuccess', selectorLabelClass: 'relSelectorLabelSuccess'
  },
  {
    value: 'investor', label: 'Investor', icon: Briefcase,
    iconBgClass: 'relIconBg', iconClass: 'relIcon',
    badgeClass: 'relBadge',
    filterActiveClass: 'relFilterActive', filterInactiveClass: 'relFilterInactive',
    selectorActiveClass: 'relSelectorActive', selectorIconClass: 'relSelectorIcon', selectorLabelClass: 'relSelectorLabel'
  },
  {
    value: 'partner', label: 'Partner', icon: Building2,
    iconBgClass: 'relIconBg', iconClass: 'relIcon',
    badgeClass: 'relBadge',
    filterActiveClass: 'relFilterActive', filterInactiveClass: 'relFilterInactive',
    selectorActiveClass: 'relSelectorActive', selectorIconClass: 'relSelectorIcon', selectorLabelClass: 'relSelectorLabel'
  }
];

const STATUS_CLASSES = {
  pending:   'statusPending',
  viewed:    'statusViewed',
  accepted:  'statusAccepted',
  declined:  'statusDeclined',
  expired:   'statusExpired',
  connected: 'statusAccepted',
  imported:  'statusImported'
};

export default function NetworkTab({ userId, vennLinks = [], connections = [], connectionsTruncated = false }) {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'connected', 'pending', 'invited'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'client', 'prospect', 'investor', 'partner'
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [inviteeName, setInviteeName] = useState('');
  const [relationshipType, setRelationshipType] = useState('client');
  const [message, setMessage] = useState('');
  const [selectedVennLink, setSelectedVennLink] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // HubSpot import state
  const [hubspotConnected, setHubspotConnected] = useState(false);
  const [showHubspotPicker, setShowHubspotPicker] = useState(false);
  const [hubspotContacts, setHubspotContacts] = useState([]);
  const [hubspotLoading, setHubspotLoading] = useState(false);
  const [hubspotSearch, setHubspotSearch] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [importingContacts, setImportingContacts] = useState(false);

  // Enhanced HubSpot import state
  const [importMode, setImportMode] = useState('select'); // 'all' | 'select'
  const [hubspotTotalCount, setHubspotTotalCount] = useState(null);
  const [hasMoreContacts, setHasMoreContacts] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [bulkRelationshipType, setBulkRelationshipType] = useState('prospect');
  const [sendInviteEmails, setSendInviteEmails] = useState(false);

  // Import job state (for "Import All" mode)
  const [importJob, setImportJob] = useState(null);
  const [importJobPolling, setImportJobPolling] = useState(false);
  const pollingIntervalRef = useRef(null);

  // Track existing emails for duplicate detection
  const [existingEmails, setExistingEmails] = useState(new Set());

  // Network list pagination state
  const [networkTotal, setNetworkTotal] = useState(0);
  const [networkHasMore, setNetworkHasMore] = useState(false);
  const [networkOffset, setNetworkOffset] = useState(0);
  const [loadingMoreNetwork, setLoadingMoreNetwork] = useState(false);
  const NETWORK_PAGE_SIZE = 50;

  useEffect(() => {
    loadInvitations();
    checkHubspotConnection();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [userId]);

  const checkHubspotConnection = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const response = await fetch(`${API_URL}/v1/hubspot/connection`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHubspotConnected(data.connected);
      }
    } catch (err) {
      console.error('Error checking HubSpot connection:', err);
    }
  };

  const loadHubspotTotalCount = async () => {
    try {
      const result = await invitationService.getHubspotContactsCount();
      setHubspotTotalCount(result.total);
    } catch (err) {
      console.error('Error loading HubSpot contacts count:', err);
    }
  };

  const loadHubspotContacts = async (searchQuery = '', cursor = null) => {
    try {
      if (cursor) {
        setLoadingMore(true);
      } else {
        setHubspotLoading(true);
      }

      const result = await invitationService.listHubspotContacts({
        limit: 50,
        after: cursor,
        search: searchQuery || undefined
      });

      if (cursor) {
        setHubspotContacts(prev => [...prev, ...(result.contacts || [])]);
      } else {
        setHubspotContacts(result.contacts || []);
      }

      setHasMoreContacts(result.hasMore || false);
      setNextCursor(result.nextCursor || null);
    } catch (err) {
      console.error('Error loading HubSpot contacts:', err);
      toast.error('Failed to load HubSpot contacts');
    } finally {
      setHubspotLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreContacts = async () => {
    if (nextCursor && !loadingMore) {
      await loadHubspotContacts(hubspotSearch, nextCursor);
    }
  };

  const handleOpenHubspotPicker = async () => {
    setShowHubspotPicker(true);
    setSelectedContacts([]);
    setHubspotSearch('');
    setImportMode('select');
    setImportJob(null);
    setBulkRelationshipType('prospect');
    setSendInviteEmails(false);
    setNextCursor(null);
    setHasMoreContacts(false);

    const emails = new Set();
    invitations.forEach(inv => {
      if (inv.invitee_email) emails.add(inv.invitee_email.toLowerCase());
    });
    connections.forEach(conn => {
      if (conn.principal?.email) emails.add(conn.principal.email.toLowerCase());
    });
    setExistingEmails(emails);

    await Promise.all([
      loadHubspotContacts(),
      loadHubspotTotalCount()
    ]);
  };

  const handleHubspotSearch = async (e) => {
    e.preventDefault();
    setNextCursor(null);
    await loadHubspotContacts(hubspotSearch);
  };

  const handleSelectAllOnPage = () => {
    const selectableContacts = hubspotContacts.filter(contact => {
      if (!contact.email) return false;
      if (existingEmails.has(contact.email.toLowerCase())) return false;
      return true;
    });

    const allSelected = selectableContacts.every(contact =>
      selectedContacts.some(c => c.id === contact.id)
    );

    if (allSelected) {
      const pageIds = new Set(selectableContacts.map(c => c.id));
      setSelectedContacts(prev => prev.filter(c => !pageIds.has(c.id)));
    } else {
      const newSelections = selectableContacts
        .filter(contact => !selectedContacts.some(c => c.id === contact.id))
        .map(contact => ({ ...contact, relationshipType: bulkRelationshipType }));
      setSelectedContacts(prev => [...prev, ...newSelections]);
    }
  };

  const handleBulkRelationshipChange = (type) => {
    setBulkRelationshipType(type);
    setSelectedContacts(prev =>
      prev.map(c => ({ ...c, relationshipType: type }))
    );
  };

  const handleStartBulkImport = async () => {
    try {
      setImportingContacts(true);
      const result = await invitationService.startHubspotBulkImport({
        relationship_type: bulkRelationshipType,
        send_emails: sendInviteEmails
      });

      setImportJob({
        id: result.jobId,
        status: 'pending',
        totalContacts: result.totalContacts,
        processedContacts: 0,
        importedContacts: 0,
        skippedContacts: 0,
        failedContacts: 0,
        progressPercent: 0
      });

      startJobPolling(result.jobId);
    } catch (err) {
      console.error('Error starting bulk import:', err);
      toast.error(err.message || 'Failed to start import');
      setImportingContacts(false);
    }
  };

  const startJobPolling = (jobId) => {
    setImportJobPolling(true);

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const status = await invitationService.getImportJobStatus(jobId);
        setImportJob(status);

        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setImportJobPolling(false);
          setImportingContacts(false);

          if (status.status === 'completed') {
            toast.success(`Import complete! ${status.importedContacts} contacts imported, ${status.skippedContacts} skipped.`);
            loadInvitations();
          } else {
            toast.error(`Import failed: ${status.errorMessage || 'Unknown error'}`);
          }
        }
      } catch (err) {
        console.error('Error polling job status:', err);
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setImportJobPolling(false);
        setImportingContacts(false);
      }
    }, 2000);
  };

  const toggleContactSelection = (contact) => {
    setSelectedContacts(prev => {
      const exists = prev.find(c => c.id === contact.id);
      if (exists) {
        return prev.filter(c => c.id !== contact.id);
      }
      return [...prev, { ...contact, relationshipType: 'prospect' }];
    });
  };

  const updateContactRelationship = (contactId, type) => {
    setSelectedContacts(prev =>
      prev.map(c => c.id === contactId ? { ...c, relationshipType: type } : c)
    );
  };

  const handleImportContacts = async (withEmails = true) => {
    if (selectedContacts.length === 0) {
      toast.error('Please select at least one contact');
      return;
    }

    try {
      setImportingContacts(true);

      const invitations = selectedContacts.map(contact => ({
        invitee_email: contact.email,
        invitee_name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.fullName || undefined,
        relationship_type: contact.relationshipType,
        hubspot_contact_id: contact.id
      }));

      await invitationService.createBulkInvitations(invitations, { send_emails: withEmails });

      const action = withEmails ? 'imported and invited' : 'imported';
      toast.success(`${selectedContacts.length} contact(s) ${action}!`);
      setShowHubspotPicker(false);
      setSelectedContacts([]);
      loadInvitations();

    } catch (err) {
      console.error('Error importing contacts:', err);
      toast.error(err.message || 'Failed to import contacts');
    } finally {
      setImportingContacts(false);
    }
  };

  const loadInvitations = async (append = false) => {
    try {
      if (append) {
        setLoadingMoreNetwork(true);
      } else {
        setLoading(true);
        setNetworkOffset(0);
      }

      const currentOffset = append ? networkOffset : 0;
      const result = await invitationService.listInvitations({
        limit: NETWORK_PAGE_SIZE,
        offset: currentOffset
      });

      if (append) {
        setInvitations(prev => [...prev, ...(result.invitations || [])]);
      } else {
        setInvitations(result.invitations || []);
      }

      setNetworkTotal(result.total || 0);
      setNetworkHasMore(result.hasMore || false);
      setNetworkOffset(currentOffset + (result.invitations?.length || 0));
    } catch (err) {
      console.error('Error loading invitations:', err);
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
      setLoadingMoreNetwork(false);
    }
  };

  const loadMoreNetwork = () => {
    if (networkHasMore && !loadingMoreNetwork) {
      loadInvitations(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!inviteeEmail.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      setSubmitting(true);

      await invitationService.createInvitation({
        invitee_email: inviteeEmail.trim(),
        invitee_name: inviteeName.trim() || undefined,
        relationship_type: relationshipType,
        venn_link_id: selectedVennLink || undefined,
        message: message.trim() || undefined
      });

      toast.success('Invitation sent!');

      setInviteeEmail('');
      setInviteeName('');
      setMessage('');
      setSelectedVennLink('');
      setShowInviteForm(false);

      loadInvitations();

    } catch (err) {
      console.error('Error sending invitation:', err);
      toast.error(err.message || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (id) => {
    try {
      await invitationService.resendInvitation(id);
      toast.success('Invitation resent!');
    } catch (err) {
      console.error('Error resending invitation:', err);
      toast.error(err.message || 'Failed to resend invitation');
    }
  };

  const handleCancel = async (id) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      await invitationService.cancelInvitation(id);
      toast.success('Invitation cancelled');
      loadInvitations();
    } catch (err) {
      console.error('Error cancelling invitation:', err);
      toast.error(err.message || 'Failed to cancel invitation');
    }
  };

  const handleSendInvite = async (id) => {
    try {
      await invitationService.resendInvitation(id);
      toast.success('Invitation sent!');
      loadInvitations();
    } catch (err) {
      console.error('Error sending invitation:', err);
      toast.error(err.message || 'Failed to send invitation');
    }
  };

  const handleUpdateRelationshipType = async (id, newType) => {
    try {
      await invitationService.updateInvitation(id, { relationship_type: newType });
      toast.success('Relationship type updated');
      loadInvitations();
    } catch (err) {
      console.error('Error updating relationship type:', err);
      toast.error(err.message || 'Failed to update relationship type');
    }
  };

  const buildNetworkList = () => {
    const network = [];

    connections.forEach(conn => {
      network.push({
        id: `connection-${conn.id}`,
        type: 'connected',
        name: conn.principal?.full_name || conn.principal?.email,
        email: conn.principal?.email,
        phone: conn.principal?.phone,
        relationshipType: conn.relationship_type || 'client',
        connectedAt: conn.opted_in_at,
        hasCalendar: !!conn.principal?.has_calendar,
        source: 'connection',
        rawData: conn
      });
    });

    invitations.forEach(inv => {
      if (inv.status === 'accepted') {
        const alreadyConnected = connections.some(c =>
          c.principal?.email?.toLowerCase() === inv.invitee_email?.toLowerCase()
        );
        if (alreadyConnected) return;
      }

      network.push({
        id: `invitation-${inv.id}`,
        type: inv.status === 'accepted' ? 'connected' : 'invited',
        status: inv.status,
        name: inv.invitee_name || inv.invitee_email,
        email: inv.invitee_email,
        relationshipType: inv.relationship_type || 'prospect',
        invitedAt: inv.created_at,
        connectedAt: inv.responded_at,
        hasCalendar: false,
        source: 'invitation',
        rawData: inv
      });
    });

    return network;
  };

  const networkList = buildNetworkList();

  const filteredNetwork = networkList.filter(item => {
    if (statusFilter === 'connected' && item.type !== 'connected') return false;
    if (statusFilter === 'pending' && (item.type !== 'invited' || !['pending', 'viewed'].includes(item.status))) return false;
    if (statusFilter === 'invited' && item.type !== 'invited') return false;

    if (typeFilter !== 'all' && item.relationshipType !== typeFilter) return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchName = item.name?.toLowerCase().includes(query);
      const matchEmail = item.email?.toLowerCase().includes(query);
      if (!matchName && !matchEmail) return false;
    }

    return true;
  });

  const loadedTotal = networkList.length;
  const actualTotal = networkTotal + connections.length;
  const stats = {
    total: networkHasMore ? actualTotal : loadedTotal,
    connected: networkList.filter(i => i.type === 'connected').length,
    pending: networkList.filter(i => i.type === 'invited' && ['pending', 'viewed'].includes(i.status)).length,
    byType: {
      client: networkList.filter(i => i.relationshipType === 'client').length,
      prospect: networkList.filter(i => i.relationshipType === 'prospect').length,
      investor: networkList.filter(i => i.relationshipType === 'investor').length,
      partner: networkList.filter(i => i.relationshipType === 'partner').length
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRelationshipInfo = (type) => {
    return RELATIONSHIP_TYPES.find(r => r.value === type) || RELATIONSHIP_TYPES[0];
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <div className={styles.spinner} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h2 className={styles.headerTitle}>Your Network</h2>
          <p className={styles.headerSubtitle}>Manage all your business connections in one place</p>
        </div>
        <div className={styles.headerActions}>
          {hubspotConnected && (
            <button onClick={handleOpenHubspotPicker} className={styles.hubspotBtn}>
              <Download size={16} />
              Import from HubSpot
            </button>
          )}
          <button onClick={() => setShowInviteForm(true)} className={styles.addNetworkBtn}>
            <UserPlus size={16} />
            Add to Network
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Total Network</p>
          <p className={styles.statValue}>{stats.total}</p>
        </div>
        <div className={`${styles.statCard} ${styles.statCardConnected}`}>
          <p className={`${styles.statLabel} ${styles.statLabelConnected}`}>Connected</p>
          <p className={`${styles.statValue} ${styles.statValueConnected}`}>{stats.connected}</p>
        </div>
        <div className={`${styles.statCard} ${styles.statCardPending}`}>
          <p className={`${styles.statLabel} ${styles.statLabelPending}`}>Pending</p>
          <p className={`${styles.statValue} ${styles.statValuePending}`}>{stats.pending}</p>
        </div>
        <div className={`${styles.statCard} ${styles.statCardCalendar}`}>
          <p className={`${styles.statLabel} ${styles.statLabelCalendar}`}>With Calendar</p>
          <p className={`${styles.statValue} ${styles.statValueCalendar}`}>
            {networkList.filter(i => i.hasCalendar).length}
          </p>
        </div>
      </div>

      {/* Relationship Type Breakdown */}
      <div className={styles.typeBreakdown}>
        <span className={styles.typeBreakdownLabel}>By type:</span>
        {RELATIONSHIP_TYPES.map(type => (
          <button
            key={type.value}
            onClick={() => setTypeFilter(typeFilter === type.value ? 'all' : type.value)}
            className={`${styles.relFilterPill} ${typeFilter === type.value ? styles[type.filterActiveClass] : styles[type.filterInactiveClass]}`}
          >
            <type.icon size={14} />
            {type.label} ({stats.byType[type.value]})
          </button>
        ))}
        {typeFilter !== 'all' && (
          <button onClick={() => setTypeFilter('all')} className={styles.clearFilter}>
            Clear filter
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className={styles.searchRow}>
        <div className={styles.searchWrap}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className={styles.searchInput}
          />
        </div>
        <div className={styles.statusFilters}>
          <button
            onClick={() => setStatusFilter('all')}
            className={`${styles.statusFilterBtn} ${statusFilter === 'all' ? styles.filterAllActive : styles.filterAll}`}
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter('connected')}
            className={`${styles.statusFilterBtn} ${statusFilter === 'connected' ? styles.filterConnectedActive : styles.filterConnected}`}
          >
            Connected
          </button>
          <button
            onClick={() => setStatusFilter('pending')}
            className={`${styles.statusFilterBtn} ${statusFilter === 'pending' ? styles.filterPendingActive : styles.filterPending}`}
          >
            Pending
          </button>
          <button onClick={loadInvitations} className={styles.refreshBtn} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Network List */}
      {connectionsTruncated && (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #888)', margin: '0 0 0.75rem' }}>
          Showing first 500 — pagination coming soon.
        </p>
      )}
      {filteredNetwork.length === 0 ? (
        <div className={styles.emptyState}>
          <Users size={48} className={styles.emptyIcon} />
          <h3 className={styles.emptyTitle}>
            {networkList.length === 0 ? 'No connections yet' : 'No matches found'}
          </h3>
          <p className={styles.emptyDesc}>
            {networkList.length === 0
              ? 'Start building your network by inviting stakeholders.'
              : 'Try adjusting your filters or search query.'}
          </p>
          {networkList.length === 0 && (
            <button onClick={() => setShowInviteForm(true)} className={styles.emptyAddBtn}>
              <UserPlus size={16} />
              Add Your First Connection
            </button>
          )}
        </div>
      ) : (
        <div className={styles.networkList}>
          {filteredNetwork.map((item) => {
            const relationshipInfo = getRelationshipInfo(item.relationshipType);
            const RelIcon = relationshipInfo.icon;

            return (
              <div key={item.id} className={styles.networkCard}>
                <div className={styles.networkCardInner}>
                  <div className={styles.networkCardLeft}>
                    <div className={styles[relationshipInfo.iconBgClass]}>
                      <RelIcon size={20} className={styles[relationshipInfo.iconClass]} />
                    </div>
                    <div className={styles.networkCardBody}>
                      <div className={styles.networkCardTitleRow}>
                        <h4 className={styles.networkCardName}>{item.name}</h4>
                        {item.type === 'connected' ? (
                          <span className={`${styles.statusBadge} ${styles.statusConnected}`}>
                            Connected
                          </span>
                        ) : (
                          <span className={`${styles.statusBadge} ${styles[STATUS_CLASSES[item.status]]}`}>
                            {item.status}
                          </span>
                        )}
                        {item.hasCalendar && (
                          <span className={styles.calendarBadge}>
                            <Calendar size={10} />
                            Calendar
                          </span>
                        )}
                      </div>
                      <p className={styles.networkCardEmail}>{item.email}</p>
                      {item.phone && (
                        <p className={styles.networkCardPhone}>{item.phone}</p>
                      )}
                      <div className={styles.networkCardMeta}>
                        <span className={styles.networkCardMetaItem}>
                          <Clock size={12} />
                          {item.type === 'connected'
                            ? `Connected ${formatDate(item.connectedAt)}`
                            : `Invited ${formatDate(item.invitedAt)}`}
                        </span>
                        <span className={styles[relationshipInfo.badgeClass]}>
                          {relationshipInfo.label}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className={styles.networkCardActions}>
                    {item.source === 'invitation' && ['pending', 'viewed'].includes(item.status) && (
                      <>
                        <button
                          onClick={() => handleResend(item.rawData.id)}
                          className={`${styles.actionIconBtn} ${styles.resendBtn}`}
                          title="Resend invitation"
                        >
                          <Send size={16} />
                        </button>
                        <button
                          onClick={() => handleCancel(item.rawData.id)}
                          className={`${styles.actionIconBtn} ${styles.cancelBtn}`}
                          title="Cancel invitation"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                    {item.source === 'invitation' && item.status === 'imported' && (
                      <>
                        <button
                          onClick={() => handleSendInvite(item.rawData.id)}
                          className={styles.inviteBtn}
                          title="Send invitation email"
                        >
                          <Send size={14} />
                          Invite
                        </button>
                        <select
                          value={item.relationshipType}
                          onChange={(e) => handleUpdateRelationshipType(item.rawData.id, e.target.value)}
                          className={styles.relTypeSelect}
                          title="Change relationship type"
                        >
                          {RELATIONSHIP_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleCancel(item.rawData.id)}
                          className={`${styles.actionIconBtn} ${styles.cancelBtn}`}
                          title="Remove contact"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                    {item.type === 'connected' && (
                      <CheckCircle size={20} className={styles.connectedIcon} />
                    )}
                    {item.status === 'declined' && (
                      <XCircle size={20} className={styles.declinedIcon} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Load More Button for Network */}
          {networkHasMore && (
            <button
              onClick={loadMoreNetwork}
              disabled={loadingMoreNetwork}
              className={styles.loadMoreBtn}
            >
              {loadingMoreNetwork ? (
                <span className={styles.loadMoreInner}>
                  <RefreshCw size={16} className={styles.spinIcon} />
                  Loading...
                </span>
              ) : (
                `Load More (${invitations.length} of ${networkTotal.toLocaleString()})`
              )}
            </button>
          )}
        </div>
      )}

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <div className={styles.modalInner}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Add to Network</h3>
                <button onClick={() => setShowInviteForm(false)} className={styles.closeBtn}>
                  <XCircle size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className={styles.form}>
                {/* Email */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Email Address *</label>
                  <input
                    type="email"
                    value={inviteeEmail}
                    onChange={(e) => setInviteeEmail(e.target.value)}
                    required
                    className={styles.formInput}
                    placeholder="contact@company.com"
                  />
                </div>

                {/* Name */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Name (Optional)</label>
                  <input
                    type="text"
                    value={inviteeName}
                    onChange={(e) => setInviteeName(e.target.value)}
                    className={styles.formInput}
                    placeholder="John Smith"
                  />
                </div>

                {/* Relationship Type */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Relationship Type</label>
                  <div className={styles.relSelectorGrid}>
                    {RELATIONSHIP_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isActive = relationshipType === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setRelationshipType(type.value)}
                          className={`${styles.relSelector} ${isActive ? styles[type.selectorActiveClass] : styles.relSelectorInactive}`}
                        >
                          <Icon size={18} className={isActive ? styles[type.selectorIconClass] : styles.relSelectorIconInactive} />
                          <span className={isActive ? styles[type.selectorLabelClass] : styles.relSelectorLabelInactive}>
                            {type.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Venn Link (Optional) */}
                {vennLinks.length > 0 && (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Include Booking Link (Optional)</label>
                    <select
                      value={selectedVennLink}
                      onChange={(e) => setSelectedVennLink(e.target.value)}
                      className={styles.formSelect}
                    >
                      <option value="">No booking link</option>
                      {vennLinks.map((link) => (
                        <option key={link.id} value={link.id}>
                          {link.title} ({link.duration_minutes} min)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Personal Message */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Personal Message (Optional)</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    className={styles.formTextarea}
                    placeholder="Add a personal note to your invitation..."
                  />
                </div>

                {/* Actions */}
                <div className={styles.formActions}>
                  <button
                    type="button"
                    onClick={() => setShowInviteForm(false)}
                    className={styles.cancelActionBtn}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !inviteeEmail.trim()}
                    className={styles.submitBtn}
                  >
                    {submitting ? (
                      <>
                        <RefreshCw size={16} className={styles.spinIcon} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* HubSpot Contact Picker Modal */}
      {showHubspotPicker && (
        <div className={styles.overlay}>
          <div className={styles.hubspotModal}>
            {/* Header with Mode Tabs */}
            <div className={styles.pickerHeader}>
              <div className={styles.pickerTitleRow}>
                <h3 className={styles.pickerTitle}>Import from HubSpot</h3>
                <button onClick={() => setShowHubspotPicker(false)} className={styles.closeBtn}>
                  <X size={20} />
                </button>
              </div>

              {/* Mode Tabs */}
              <div className={styles.modeTabs}>
                <button
                  onClick={() => setImportMode('all')}
                  className={`${styles.modeTabBase} ${importMode === 'all' ? styles.modeTabActive : styles.modeTabInactive}`}
                >
                  Import All Contacts
                </button>
                <button
                  onClick={() => setImportMode('select')}
                  className={`${styles.modeTabBase} ${importMode === 'select' ? styles.modeTabActive : styles.modeTabInactive}`}
                >
                  Select Contacts
                </button>
              </div>

              {/* Search (only in select mode) */}
              {importMode === 'select' && (
                <form onSubmit={handleHubspotSearch} className={styles.pickerSearchForm}>
                  <div className={styles.pickerSearchWrap}>
                    <Search size={18} className={styles.pickerSearchIcon} />
                    <input
                      type="text"
                      value={hubspotSearch}
                      onChange={(e) => setHubspotSearch(e.target.value)}
                      placeholder="Search contacts by name or email..."
                      className={styles.pickerSearchInput}
                    />
                  </div>
                  <button type="submit" className={styles.pickerSearchBtn}>Search</button>
                </form>
              )}
            </div>

            {/* Import All Mode */}
            {importMode === 'all' && (
              <div className={styles.importAllSection}>
                {importJob ? (
                  <div className={styles.importJobProgress}>
                    <div className={styles.importJobCenter}>
                      <div className={styles.importJobIconWrap}>
                        {importJob.status === 'completed' ? (
                          <CheckCircle size={32} className={styles.connectedIcon} />
                        ) : importJob.status === 'failed' ? (
                          <XCircle size={32} className={styles.declinedIcon} />
                        ) : (
                          <RefreshCw size={32} className={`${styles.accentIcon} ${styles.spinIcon}`} />
                        )}
                      </div>
                      <h4 className={styles.importJobTitle}>
                        {importJob.status === 'completed' ? 'Import Complete!' :
                         importJob.status === 'failed' ? 'Import Failed' :
                         'Importing Contacts...'}
                      </h4>
                      <p className={styles.importJobDesc}>
                        {importJob.status === 'running' && `Processing ${importJob.processedContacts} of ${importJob.totalContacts} contacts`}
                        {importJob.status === 'completed' && `${importJob.importedContacts} imported, ${importJob.skippedContacts} skipped`}
                        {importJob.status === 'failed' && importJob.errorMessage}
                      </p>
                    </div>

                    <div className={styles.progressTrack}>
                      <div
                        className={`${styles.progressBar} ${
                          importJob.status === 'completed' ? styles.progressBarSuccess :
                          importJob.status === 'failed' ? styles.progressBarError : ''
                        }`}
                        style={{ width: `${importJob.progressPercent || 0}%` }}
                      />
                    </div>

                    <div className={styles.importStatsGrid}>
                      <div className={`${styles.importStatCard} ${styles.importStatCardGreen}`}>
                        <p className={`${styles.importStatValue} ${styles.importStatValueGreen}`}>{importJob.importedContacts || 0}</p>
                        <p className={styles.importStatLabel}>Imported</p>
                      </div>
                      <div className={`${styles.importStatCard} ${styles.importStatCardAmber}`}>
                        <p className={`${styles.importStatValue} ${styles.importStatValueAmber}`}>{importJob.skippedContacts || 0}</p>
                        <p className={styles.importStatLabel}>Skipped</p>
                      </div>
                      <div className={`${styles.importStatCard} ${styles.importStatCardRed}`}>
                        <p className={`${styles.importStatValue} ${styles.importStatValueRed}`}>{importJob.failedContacts || 0}</p>
                        <p className={styles.importStatLabel}>Failed</p>
                      </div>
                    </div>

                    {importJob.status === 'completed' && (
                      <button onClick={() => setShowHubspotPicker(false)} className={styles.doneBtn}>
                        Done
                      </button>
                    )}
                  </div>
                ) : (
                  <div className={styles.importConfigSection}>
                    <div className={styles.importConfigCenter}>
                      <div className={styles.importConfigIconWrap}>
                        <Download size={32} className={styles.importConfigIcon} />
                      </div>
                      <h4 className={styles.importConfigTitle}>Import All HubSpot Contacts</h4>
                      <p className={styles.importConfigDesc}>
                        {hubspotTotalCount !== null
                          ? `${hubspotTotalCount.toLocaleString()} contacts will be imported`
                          : 'Loading contact count...'}
                      </p>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Default Relationship Type</label>
                      <div className={styles.bulkTypeGrid}>
                        {RELATIONSHIP_TYPES.map((type) => {
                          const Icon = type.icon;
                          const isActive = bulkRelationshipType === type.value;
                          return (
                            <button
                              key={type.value}
                              type="button"
                              onClick={() => setBulkRelationshipType(type.value)}
                              className={`${styles.bulkTypeBtn} ${isActive ? styles.bulkTypeBtnActive : ''}`}
                            >
                              <Icon size={18} className={isActive ? styles.bulkTypeBtnIcon : styles.bulkTypeBtnIconInactive} />
                              <span className={isActive ? styles.bulkTypeBtnLabel : styles.bulkTypeBtnLabelInactive}>
                                {type.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className={styles.sendEmailsOption}>
                      <input
                        type="checkbox"
                        id="sendEmails"
                        checked={sendInviteEmails}
                        onChange={(e) => setSendInviteEmails(e.target.checked)}
                        className={styles.sendEmailsCheckbox}
                      />
                      <label htmlFor="sendEmails" className={styles.sendEmailsLabel}>
                        <span className={styles.sendEmailsLabelStrong}>Send invitation emails</span>
                        <p>If unchecked, contacts will be imported without sending emails. You can invite them later.</p>
                      </label>
                    </div>

                    <button
                      onClick={handleStartBulkImport}
                      disabled={importingContacts || hubspotTotalCount === null}
                      className={styles.startImportBtn}
                    >
                      {importingContacts ? (
                        <>
                          <RefreshCw size={16} className={styles.spinIcon} />
                          Starting Import...
                        </>
                      ) : (
                        <>
                          <Download size={16} />
                          Start Import
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Select Contacts Mode */}
            {importMode === 'select' && (
              <>
                <div className={styles.pickerBody}>
                  {hubspotLoading ? (
                    <div className={styles.pickerLoading}>
                      <div className={styles.spinner} />
                    </div>
                  ) : hubspotContacts.length === 0 ? (
                    <div className={styles.pickerEmpty}>
                      <Users size={48} className={`${styles.pickerEmptyIcon} ${styles.emptyIcon}`} />
                      <p>No contacts found</p>
                    </div>
                  ) : (
                    <div className={styles.contactList}>
                      <div className={styles.selectAllHeader}>
                        <label className={styles.selectAllCheckboxLabel}>
                          <input
                            type="checkbox"
                            checked={hubspotContacts.filter(c => c.email && !existingEmails.has(c.email.toLowerCase())).every(c => selectedContacts.some(s => s.id === c.id))}
                            onChange={handleSelectAllOnPage}
                            className={styles.contactCheckbox}
                          />
                          Select all on this page
                        </label>
                        <select
                          value={bulkRelationshipType}
                          onChange={(e) => handleBulkRelationshipChange(e.target.value)}
                          className={styles.contactRelSelect}
                        >
                          {RELATIONSHIP_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>

                      {hubspotContacts.map((contact) => {
                        const isSelected = selectedContacts.find(c => c.id === contact.id);
                        const selectedContact = selectedContacts.find(c => c.id === contact.id);
                        const isAlreadyImported = existingEmails.has(contact.email?.toLowerCase());

                        return (
                          <div
                            key={contact.id}
                            className={`${styles.contactItem} ${
                              isAlreadyImported
                                ? styles.contactItemDisabled
                                : isSelected
                                  ? styles.contactItemSelected
                                  : styles.contactItemDefault
                            }`}
                            onClick={() => !isAlreadyImported && toggleContactSelection(contact)}
                          >
                            <div className={styles.contactRow}>
                              <div className={styles.contactLeft}>
                                <input
                                  type="checkbox"
                                  checked={!!isSelected}
                                  disabled={isAlreadyImported}
                                  onChange={() => {}}
                                  className={styles.contactCheckbox}
                                />
                                <div>
                                  <div className={styles.networkCardTitleRow}>
                                    <p className={styles.contactName}>
                                      {contact.firstName || contact.lastName
                                        ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
                                        : contact.fullName || 'No name'}
                                    </p>
                                    {isAlreadyImported && (
                                      <span className={styles.alreadyImportedBadge}>Already imported</span>
                                    )}
                                  </div>
                                  <p className={styles.contactEmail}>{contact.email}</p>
                                </div>
                              </div>

                              {isSelected && !isAlreadyImported && (
                                <select
                                  value={selectedContact?.relationshipType || 'prospect'}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    updateContactRelationship(contact.id, e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className={styles.contactRelSelect}
                                >
                                  {RELATIONSHIP_TYPES.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {hasMoreContacts && (
                        <button
                          onClick={loadMoreContacts}
                          disabled={loadingMore}
                          className={styles.loadMoreBtn}
                        >
                          {loadingMore ? (
                            <span className={styles.loadMoreInner}>
                              <RefreshCw size={16} className={styles.spinIcon} />
                              Loading...
                            </span>
                          ) : (
                            'Load More Contacts'
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className={styles.pickerFooter}>
                  <div className={styles.pickerFooterInner}>
                    <p className={styles.pickerFooterText}>
                      {selectedContacts.length} contact(s) selected
                      {hubspotTotalCount !== null && ` of ${hubspotTotalCount.toLocaleString()} total`}
                    </p>
                    <div className={styles.pickerFooterActions}>
                      <button onClick={() => setShowHubspotPicker(false)} className={styles.pickerCancelBtn}>
                        Cancel
                      </button>
                      <button
                        onClick={() => handleImportContacts(false)}
                        disabled={selectedContacts.length === 0 || importingContacts}
                        className={styles.importOnlyBtn}
                      >
                        Import Only
                      </button>
                      <button
                        onClick={() => handleImportContacts(true)}
                        disabled={selectedContacts.length === 0 || importingContacts}
                        className={styles.importInviteBtn}
                      >
                        {importingContacts ? (
                          <>
                            <RefreshCw size={16} className={styles.spinIcon} />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            Import & Invite
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
